import { Link } from "react-router-dom";
import { FiEye } from "react-icons/fi";
import { BiBed, BiBath, BiCar, BiShapeSquare, BiEdit, BiTrash, BiCopy } from "react-icons/bi";
import { isDraftStatus, PROPERTY_STATUS_LABEL, PropertyStatus } from "../../global/propertyStatus";
import { formatAreaForList, formatCountForList } from "../../utils/propertyListRowFormat";

type StatusFields = {
  is_rented: number;
  is_draft?: boolean;
  status?: string | null;
  status_name?: string | null;
};

type FeatureFields = {
  area?: number | string | null;
  rooms?: number | null;
  bathrooms?: number | null;
  garages?: number | null;
};

const BADGE_BASE =
  "inline-flex w-fit items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-tight";

export function PropertyStatusBadge({ property }: { property: StatusFields }) {
  if (property.is_draft || isDraftStatus(property.status_name) || isDraftStatus(property.status)) {
    return (
      <span className={`${BADGE_BASE} border-amber-200 bg-amber-50 text-amber-800`}>
        {PROPERTY_STATUS_LABEL[PropertyStatus.PRE_REGISTERED]}
      </span>
    );
  }

  if (property.is_rented) {
    return <span className={`${BADGE_BASE} border-slate-200 bg-slate-100 text-slate-700`}>Inativo</span>;
  }

  return <span className={`${BADGE_BASE} border-emerald-200 bg-emerald-50 text-emerald-800`}>Ativo</span>;
}

/** Área, quartos, banheiros e vagas. */
export function PropertyFeatures({ property }: { property: FeatureFields }) {
  const items = [
    { icon: BiShapeSquare, label: "Área", value: formatAreaForList(property.area) },
    { icon: BiBed, label: "Quartos", value: formatCountForList(property.rooms ?? undefined) },
    { icon: BiBath, label: "Banheiros", value: formatCountForList(property.bathrooms ?? undefined) },
    { icon: BiCar, label: "Vagas", value: formatCountForList(property.garages ?? undefined) },
  ];

  return (
    <div className="flex flex-nowrap items-center gap-x-1.5 overflow-hidden text-slate-500">
      {items.map(({ icon: Icon, label, value }) => (
        <span key={label} className="inline-flex items-center gap-0.5" title={label}>
          <Icon className="shrink-0" size={15} aria-hidden />
          <span className="text-slate-600">{value}</span>
        </span>
      ))}
    </div>
  );
}

type ActionsProps = {
  propertyId: number;
  onClone: () => void;
  onRemove: () => void;
  /** Cards (mobile) get bigger touch targets. */
  touch?: boolean;
};

export function PropertyRowActions({ propertyId, onClone, onRemove, touch = false }: ActionsProps) {
  const button = `inline-flex shrink-0 items-center justify-center rounded-lg transition ${touch ? "h-10 w-10" : "p-1"}`;
  const size = touch ? 19 : 16;

  return (
    <div className={`flex flex-nowrap items-center justify-end ${touch ? "gap-1" : "gap-0.5"}`}>
      <Link
        to={`/property/${propertyId}/details`}
        title="Ver detalhes"
        aria-label={`Ver detalhes do imóvel ${propertyId}`}
        className={`${button} text-sky-500 hover:bg-sky-50`}
      >
        <FiEye size={size} />
      </Link>
      <Link
        to={`/property/${propertyId}`}
        title="Editar"
        aria-label={`Editar o imóvel ${propertyId}`}
        className={`${button} text-cyan-500 hover:bg-cyan-50`}
      >
        <BiEdit size={size} />
      </Link>
      <button
        type="button"
        title="Clonar"
        aria-label={`Clonar o imóvel ${propertyId}`}
        onClick={onClone}
        className={`${button} border-0 bg-transparent text-blue-900 hover:bg-blue-50`}
      >
        <BiCopy size={size} />
      </button>
      <button
        type="button"
        title="Excluir"
        aria-label={`Excluir o imóvel ${propertyId}`}
        onClick={onRemove}
        className={`${button} border-0 bg-transparent text-red-500 hover:bg-red-50`}
      >
        <BiTrash size={size} />
      </button>
    </div>
  );
}
